import type { PortalLocale } from '@worldcoo/monolith/portals';
import { useRouteContext, useRouter } from '@tanstack/react-router';
import { i18n, setupI18n, type I18n } from '@lingui/core';
import { getRequestContext } from './data';
import { refreshRouterContextForLocale } from './router-context-refresh';
import { updateRouterLocale } from '@/router';
import { buildLanguagePath } from './language-path';

export function useTranslation() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const router = useRouter();
	// The output rewrite strips the locale prefix on the `ngos` host, so `/<lang>/…` would be a dead route.
	const isNgoAdmin = portalId === 'ngos';

	const changeLanguage = (lang: PortalLocale, currentCampaignId?: string) => {
		loadLandingTranslation(lang);
		if (typeof document !== 'undefined') {
			document.cookie = `language=${lang}; path=/; max-age=31536000`;
		}
		updateRouterLocale(lang);
		if (router.options.context) {
			(router.options.context as { locale: PortalLocale }).locale = lang;
		}

		if (typeof window !== 'undefined' && !isNgoAdmin) {
			const newPath = buildLanguagePath(
				lang,
				window.location.pathname,
				window.location.search,
				locale,
				portalId,
				router,
				currentCampaignId,
			);
			router.history.push(newPath);
		}

		// No URL change re-renders the admin app.
		if (isNgoAdmin) {
			router.invalidate();
		}

		// Not awaited: it only catches the context up. no-floating-promises is off here, hence TEC-3888.
		void refreshRouterContextForLocale(lang, router.options.context, (locale) => getRequestContext({ data: locale }));
	};

	return { language: locale, changeLanguage };
}

/** The href per locale option, so the language switcher renders real `<a>` links. */
export function useLanguageHrefs(locales: readonly PortalLocale[]): Record<string, string> {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const router = useRouter();

	// Not `window.location`: a server/client branch here is a hydration mismatch.
	const { pathname, search } = router.history.location;

	const hrefs: Record<string, string> = {};
	for (const lang of locales) {
		hrefs[lang] = buildLanguagePath(lang, pathname, search, locale, portalId, router);
	}
	return hrefs;
}

export const landingLocales = ['es', 'en', 'ca', 'it'] as const;
export type LandingLocale = (typeof landingLocales)[number];

/**
 * One per request on the server: concurrent renders sharing a catalog emit `<html lang="es">` over a body in
 * another language, a #418 seen almost only from crawlers.
 */
export function createLocaleI18n(): I18n {
	return typeof document === 'undefined' ? setupI18n() : i18n;
}

/** Only the given instance: activating the server's global per request clobbers concurrent renders. */
export async function loadLandingTranslation(locale: PortalLocale, instance?: I18n) {
	const { messages } = await import(`../locales/${locale}.po`);
	const target = instance ?? i18n;
	target.loadAndActivate({ locale, messages });
	return target;
}
